/**
 * Redação, na telemetria do Elastic APM RUM, dos valores do login social que valem como credencial:
 * a chave de resgate de uso único, que viaja como segmento de path (`/social-callback/{chave}` no
 * front de login e `/auth/social/token/{chave}` na API), o `code`/`state` do callback OAuth, que
 * viajam na query de `/auth/social/{provider}/callback`, e o `binding` (hash do verifier da aba),
 * que viaja na query de `/auth/social/{provider}/start`.
 *
 * Espelha a semântica do `SocialAuthTelemetryRedactor` do backend: a redação é aditiva e escopada,
 * e `code`/`state`/`binding` só são tocados em URLs com `/auth/social/`.
 */

export const APM_REDACTED = "[REMOVED]";

const AUTH_SOCIAL_SEGMENT = "/auth/social/";

// O segmento que segue o prefixo vai até o próximo "/", "?", "#" ou espaço (o nome de um span HTTP
// é "GET <url>").
const REDEMPTION_KEY_PATTERN = /(\/social\/token\/|\/social-callback\/)[^/?#\s]+/gi;

// O delimitador "?" ou "&" ancora o nome do parâmetro, para que `postcode=` não case com `code=`.
const OAUTH_PARAMETER_PATTERN = /([?&](?:code|state|binding)=)[^&#\s]*/gi;

interface ApmPageContext {
  page?: { url?: unknown; referer?: unknown };
}

interface ApmHttpContext {
  http?: { url?: unknown };
}

interface ApmSpan {
  name?: unknown;
  context?: ApmHttpContext;
}

interface ApmTransaction {
  name?: unknown;
  context?: ApmPageContext;
  spans?: unknown;
}

interface ApmError {
  context?: ApmPageContext;
  exception?: { message?: unknown };
}

interface ApmRedactablePayload {
  transactions?: unknown;
  errors?: unknown;
}

/**
 * Substitui por `[REMOVED]` o segmento depois de `/social-callback/` e de `/social/token/` e, quando
 * a URL é de uma rota `/auth/social/`, os valores de `code`, `state` e `binding`. O resto da URL
 * (inclusive o `returnUrl`) fica intacto. Serve também a texto que embute uma URL, como a mensagem
 * de um `HttpErrorResponse`.
 */
export function redactSocialAuthUrl(url: string): string {
  const redacted = url.replace(
    REDEMPTION_KEY_PATTERN,
    (_match: string, prefix: string) => `${prefix}${APM_REDACTED}`
  );

  if (!redacted.toLowerCase().includes(AUTH_SOCIAL_SEGMENT)) {
    return redacted;
  }

  return redacted.replace(
    OAUTH_PARAMETER_PATTERN,
    (_match: string, prefix: string) => `${prefix}${APM_REDACTED}`
  );
}

/**
 * Filtro do APM (`apm.addFilter`): redige no próprio payload o nome, a URL e o referer de página das
 * transações, o nome e a URL HTTP dos spans e a URL, o referer e a mensagem da exceção dos erros, e
 * devolve o mesmo payload, que é o contrato de um filtro. Tolera campos ausentes ou fora do formato.
 */
export function redactApmPayload<T>(payload: T): T {
  if (typeof payload !== "object" || payload === null) {
    return payload;
  }

  const { transactions, errors } = payload as ApmRedactablePayload;

  if (Array.isArray(transactions)) {
    for (const transaction of transactions as (ApmTransaction | null)[]) {
      redactTransaction(transaction);
    }
  }

  if (Array.isArray(errors)) {
    for (const error of errors as (ApmError | null)[]) {
      redactError(error);
    }
  }

  return payload;
}

function redactError(error: ApmError | null): void {
  if (!error) {
    return;
  }

  redactPageUrl(error.context);

  // A mensagem de um HttpErrorResponse do Angular embute a URL completa da chamada.
  const exception = error.exception;

  if (typeof exception?.message === "string") {
    exception.message = redactSocialAuthUrl(exception.message);
  }
}

function redactTransaction(transaction: ApmTransaction | null): void {
  if (!transaction) {
    return;
  }

  if (typeof transaction.name === "string") {
    transaction.name = redactSocialAuthUrl(transaction.name);
  }

  redactPageUrl(transaction.context);

  if (!Array.isArray(transaction.spans)) {
    return;
  }

  for (const span of transaction.spans as (ApmSpan | null)[]) {
    redactSpan(span);
  }
}

function redactSpan(span: ApmSpan | null): void {
  if (!span) {
    return;
  }

  if (typeof span.name === "string") {
    span.name = redactSocialAuthUrl(span.name);
  }

  const http = span.context?.http;

  if (typeof http?.url === "string") {
    http.url = redactSocialAuthUrl(http.url);
  }
}

function redactPageUrl(context: ApmPageContext | undefined): void {
  const page = context?.page;

  if (typeof page?.url === "string") {
    page.url = redactSocialAuthUrl(page.url);
  }

  // Quem chega ao callback vindo do provedor ou do start traz a URL anterior no referer.
  if (typeof page?.referer === "string") {
    page.referer = redactSocialAuthUrl(page.referer);
  }
}
